import { ContentHeader } from "../components/ContentHeader";
import { useUI } from "../data/UIContext";
import { useData } from "../data/DataContext";
import { useApi } from "../hooks/useApi";

type Grade = "A" | "B" | "C" | "D" | "F";

interface ScorecardService {
  service: string;
  tier: string | null;
  team: string | null;
  estate: string | null;
  coverage_score: number;
  grade: Grade;
  slo_status: string | null;
  dora: { change_failure_rate: number | null; mttr_minutes: number | null } | null;
  signals: { alert: boolean; runbook: boolean; slo: boolean; dashboard: boolean; owner: boolean };
}
interface ScorecardResponse {
  status: "ok" | "unavailable";
  reason?: string;
  services: ScorecardService[];
  summary: { distribution?: Record<Grade, number>; avg_coverage?: number; total?: number };
}

const GRADE_COLOR: Record<Grade, string> = {
  A: "var(--green)", B: "var(--green)", C: "var(--yellow)", D: "var(--red)", F: "var(--red)",
};

// Shown on hover (title) so the letters aren't a mystery.
const GRADE_HELP: Record<Grade, string> = {
  A: "A · 90–100 — excellent: strong observability coverage, SLO budget healthy",
  B: "B · 75–89 — good",
  C: "C · 60–74 — fair, needs attention",
  D: "D · 40–59 — poor: notable gaps",
  F: "F · below 40 — critical: major observability gaps",
};
const SCORECARD_LEGEND =
  "Grade = the service's observability-coverage score (0–100 from the Coverage matrix), " +
  "dropped one letter if any of its SLOs is burning. A ≥90 · B 75–89 · C 60–74 · D 40–59 · F <40. " +
  "avg coverage = mean coverage score across all services.";

export function ScorecardTab() {
  const ui = useUI();
  const data = useData();
  const { data: res, loading, error } = useApi<ScorecardResponse>("/command-center/scorecard");
  const title = "Ops-Excellence Scorecard";
  const desc = "One maturity grade per service — coverage + SLO budget + DORA, worst first.";

  if (loading) {
    return (<><ContentHeader title={title} description={desc} />
      <div className="loading"><div className="loading-spinner" /><p>Composing scorecard…</p></div></>);
  }
  if (error || !res) {
    return (<><ContentHeader title={title} description={desc} />
      <div className="empty">Could not load the scorecard. Is command-center-api running?</div></>);
  }
  if (res.status === "unavailable") {
    return (<><ContentHeader title={title} description={desc} />
      <div className="empty">{res.reason ?? "coverage.json not available."}</div></>);
  }

  const byName = data.coverageByName; // for opening the entity (side panel) on click
  const openEntity = (name: string) => {
    const all = [
      ...(data.commandCenter?.estates.backend ?? []),
      ...(data.commandCenter?.estates.surfaces ?? []),
    ];
    const ent = all.find((e) => e.name === name);
    if (ent) ui.openEntity(ent);
  };

  const dist = res.summary.distribution ?? ({} as Record<Grade, number>);

  return (
    <>
      <ContentHeader title={title} count={res.summary.total} description={desc} />

      <div className="cov-summary">
        {(["A", "B", "C", "D", "F"] as Grade[]).map((g) => (
          <span key={g} className="cov-pill" title={GRADE_HELP[g]} style={{ cursor: "help" }}>
            <span className="dot" style={{ background: GRADE_COLOR[g] }} />
            <strong>{dist[g] ?? 0}</strong> {g}
          </span>
        ))}
        <span className="cov-pill" title="Mean observability-coverage score (0–100) across all services" style={{ cursor: "help" }}><strong>{res.summary.avg_coverage ?? 0}</strong> avg coverage</span>
      </div>

      <p className="legend-note">{SCORECARD_LEGEND}</p>

      <table className="cov-matrix">
        <thead>
          <tr>
            <th>Service</th>
            <th title="Service criticality tier — P0 (most critical) to P3">Tier</th>
            <th>Team</th><th>Grade</th>
            <th title="Observability-coverage score, 0–100, from the Coverage matrix">Coverage</th>
            <th title="Worst SLO error-budget status for this service">SLO</th>
            <th title="Change Failure Rate (DORA) — % of this team's deploys that caused an incident">CFR</th>
            <th title="Mean Time To Recovery (DORA) — this team's average incident resolution time">MTTR</th>
            <th title="Coverage signals present: Alert · Runbook · SLO · Dashboard · Owner (green = yes)">Signals</th>
          </tr>
        </thead>
        <tbody>
          {res.services.map((s) => (
            <tr key={s.service}>
              <td>
                <button className="cov-name" onClick={() => openEntity(s.service)}
                        disabled={!byName[s.service] && !s.service}>{s.service}</button>
              </td>
              <td>{s.tier ? <span className={`tier-badge tier-${s.tier}`}>{s.tier}</span> : "—"}</td>
              <td style={{ color: "var(--text-muted)" }}>{s.team ?? "—"}</td>
              <td><span className="grade-badge" style={{ color: GRADE_COLOR[s.grade], borderColor: GRADE_COLOR[s.grade], cursor: "help" }}
                        title={`${GRADE_HELP[s.grade]}. This service: coverage ${s.coverage_score}/100${(s.slo_status === "fast-burn" || s.slo_status === "exhausted") ? " · SLO burning (−1 grade)" : ""}.`}>{s.grade}</span></td>
              <td className="cov-cell" style={{ fontFamily: "var(--font-mono)" }}>{s.coverage_score}</td>
              <td style={{ color: sloColor(s.slo_status), fontSize: 11.5 }}>{sloLabel(s.slo_status)}</td>
              <td className="cov-cell">{s.dora?.change_failure_rate != null ? `${Math.round(s.dora.change_failure_rate * 100)}%` : "—"}</td>
              <td className="cov-cell">{s.dora?.mttr_minutes != null ? `${s.dora.mttr_minutes}m` : "—"}</td>
              <td>
                <span className="sig-dots" title="alert · runbook · SLO · dashboard · owner">
                  {([["A", s.signals.alert], ["R", s.signals.runbook], ["S", s.signals.slo],
                     ["D", s.signals.dashboard], ["O", s.signals.owner]] as [string, boolean][]).map(([k, on]) => (
                    <span key={k} className="sig-dot" style={{ background: on ? "var(--green)" : "var(--border-strong)" }} title={k} />
                  ))}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function sloLabel(s: string | null): string {
  if (!s) return "—";
  return ({ "fast-burn": "fast burn", "exhausted": "exhausted", "slow-burn": "slow burn", "ok": "healthy", "no-data": "no data" } as Record<string, string>)[s] ?? s;
}
function sloColor(s: string | null): string {
  if (s === "ok") return "var(--green)";
  if (s === "slow-burn") return "var(--yellow)";
  if (s === "fast-burn" || s === "exhausted") return "var(--red)";
  return "var(--text-faint)";
}
